import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon, MoreHorizontalIcon, ArchiveIcon, TrashIcon, UserIcon, PhoneIcon, MapPinIcon, CreditCardIcon, TagIcon, FileTextIcon, AlertTriangleIcon, ClockIcon, PackageIcon, TruckIcon, CalendarIcon, CheckIcon } from 'lucide-react';
import { Order, OrderStatus } from './Orders';
export function OrderDetail() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Mock data based on the ID or default
  const order: Order = {
    id: id || '220629-1e753',
    status: 'new',
    paymentStatus: 'paid',
    customer: {
      name: 'randy',
      email: 'randy@randy.com',
      address: 'randy',
      mobileNumber: '6596605522',
      additionalPhone: '1234',
      address2: '1234'
    },
    items: [{
      name: 'Kittens',
      quantity: 2,
      price: 111.0
    }],
    total: 222.0,
    date: '29/06/2022',
    time: '14:28',
    transaction: '62bc1b4be3c2250009f1e753',
    transactionStatus: 'confirmed',
    notes: 'Please deliver between 9 AM and 5 PM.',
    shopId: '1',
    shopName: 'Beach Shop'
  };
  const getStatusBadge = (status: OrderStatus) => {
    const styles = {
      new: 'bg-blue-50 text-blue-700 border-blue-200',
      'in-progress': 'bg-amber-50 text-amber-700 border-amber-200',
      open: 'bg-purple-50 text-purple-700 border-purple-200',
      completed: 'bg-green-50 text-green-700 border-green-200',
      cancelled: 'bg-red-50 text-red-700 border-red-200'
    };
    const labels = {
      new: 'New',
      'in-progress': 'In Progress',
      open: 'Open',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${styles[status]}`} data-id="element-12342">
        {labels[status]}
      </span>;
  };
  const getPaymentMethod = (transaction?: string) => {
    if (!transaction) return 'Unknown payment method';
    if (transaction.startsWith('62')) return 'Credit Card';
    return 'Bank Transfer';
  };
  const getShippingStatus = (status: OrderStatus) => {
    switch (status) {
      case 'new':
        return 'Awaiting processing';
      case 'in-progress':
        return 'Processing';
      case 'open':
        return 'Ready for pickup';
      case 'completed':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };
  const formatDate = (dateString: string) => {
    const parts = dateString.split('/');
    if (parts.length !== 3) return dateString;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const year = parseInt(parts[2]);
    return `${day} ${months[month]} ${year}`;
  };
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-12343">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-12344">
        <div className="flex items-center gap-4" data-id="element-12345">
          <button onClick={() => navigate('/orders')} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-12346">
            <ArrowLeftIcon size={18} data-id="element-12347" />
          </button>
          <div data-id="element-12348">
            <div className="flex items-center gap-3" data-id="element-12349">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-12350">
                {t('pages.OrderDetail.order')}
                {order.id}
              </h1>
              {getStatusBadge(order.status)}
            </div>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-12351">
              {order.customer.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-12352">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-12353">
            <MailIcon size={16} data-id="element-12354" />
            {t('pages.OrderDetail.send')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-12355">
            <DownloadIcon size={16} data-id="element-12356" />
            {t('pages.OrderDetail.download')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12357">
            <CheckCircleIcon size={16} data-id="element-12358" />
            {t('pages.OrderDetail.mark_fulfilled')}
          </button>
          <div className="relative" data-id="element-12359">
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} aria-label="More actions" aria-expanded={showMoreMenu} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-12360">
              <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-12361" />
            </button>

            {showMoreMenu && <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} data-id="element-12362" />

                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-12363">
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12364">
                    <PrinterIcon size={16} className="mr-3 text-gray-400" data-id="element-12365" />
                    {t('pages.OrderDetail.print_order')}
                  </button>
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12366">
                    <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-12367" />
                    {t('pages.OrderDetail.edit_order')}
                  </button>
                  <div className="border-t border-gray-100 my-1" data-id="element-12368" />
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-12369">
                    <TrashIcon size={16} className="mr-3" data-id="element-12370" />
                    {t('pages.OrderDetail.cancel_order')}
                  </button>
                </div>
              </>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-12371">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-12372">
          {/* Order Items */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12373">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-12374">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12375">
                <PackageIcon size={18} className="text-gray-500" data-id="element-12376" />
                {t('pages.OrderDetail.order_items')}
              </h2>
            </div>
            <div className="p-6" data-id="element-12377">
              <div className="space-y-4" data-id="element-12378">
                {order.items.map((item, index) => <div key={index} className="flex justify-between border-b border-gray-100 pb-4 last:border-0 last:pb-0" data-id="element-12379">
                    <div data-id="element-12380">
                      <div className="font-medium text-gray-900" data-id="element-12381">
                        {item.name}
                      </div>
                      <div className="text-sm text-gray-500 mt-1" data-id="element-12382">
                        {t('pages.OrderDetail.quantity')} {item.quantity} ×{' '}
                        {formatCurrencyAmount(item.price, {
                      fractionDigits: 2
                    })}
                      </div>
                    </div>
                    <div className="font-medium text-gray-900" data-id="element-12383">
                      {formatCurrencyAmount(item.price * item.quantity, {
                    fractionDigits: 2
                  })}
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 border-t border-gray-100 pt-4 space-y-3" data-id="element-12384">
                <div className="flex justify-between text-sm" data-id="element-12385">
                  <span className="text-gray-500" data-id="element-12386">
                    {t('pages.OrderDetail.subtotal')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-12387">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-12388">
                  <span className="text-gray-500" data-id="element-12389">
                    {t('pages.OrderDetail.shipping')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-12390">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-12391">
                  <span className="text-gray-500" data-id="element-12392">
                    {t('pages.OrderDetail.tax')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-12393">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-base font-semibold pt-3 border-t border-gray-100" data-id="element-12394">
                  <span className="text-gray-900" data-id="element-12395">
                    {t('pages.OrderDetail.total')}
                  </span>
                  <span className="text-[#9F140F]" data-id="element-12396">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12397">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12398">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12399">
                <ClockIcon size={18} className="text-gray-500" data-id="element-12400" />
                {t('pages.OrderDetail.order_timeline')}
              </h2>
            </div>
            <div className="p-6" data-id="element-12401">
              <div className="space-y-6" data-id="element-12402">
                <div className="flex items-start" data-id="element-12403">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-12404">
                    <CheckIcon size={14} className="text-green-600" data-id="element-12405" />
                  </div>
                  <div className="ml-4" data-id="element-12406">
                    <p className="text-sm font-medium text-gray-900" data-id="element-12407">
                      {t('pages.OrderDetail.order_placed')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-12408">
                      {formatDate(order.date)} {t('pages.OrderDetail.at')}{' '}
                      {order.time}
                    </p>
                  </div>
                </div>

                {order.status === 'in-progress' || order.status === 'completed' ? <div className="flex items-start" data-id="element-12409">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mt-0.5" data-id="element-12410">
                      <CheckIcon size={14} className="text-amber-600" data-id="element-12411" />
                    </div>
                    <div className="ml-4" data-id="element-12412">
                      <p className="text-sm font-medium text-gray-900" data-id="element-12413">
                        {t('pages.OrderDetail.processing_started')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-12414">
                        {t('pages.OrderDetail.order_is_being_processed')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-12415">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-12416">
                      <span className="text-xs font-medium text-gray-500" data-id="element-12417">
                        2
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-12418">
                      <p className="text-sm font-medium text-gray-500" data-id="element-12419">
                        {t('pages.OrderDetail.processing')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-12420">
                        {t('pages.OrderDetail.waiting_to_be_processed')}
                      </p>
                    </div>
                  </div>}

                {order.status === 'completed' ? <div className="flex items-start" data-id="element-12421">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-12422">
                      <CheckIcon size={14} className="text-green-600" data-id="element-12423" />
                    </div>
                    <div className="ml-4" data-id="element-12424">
                      <p className="text-sm font-medium text-gray-900" data-id="element-12425">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-12426">
                        {t('pages.OrderDetail.order_has_been_fulfilled')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-12427">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-12428">
                      <span className="text-xs font-medium text-gray-500" data-id="element-12429">
                        3
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-12430">
                      <p className="text-sm font-medium text-gray-500" data-id="element-12431">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-12432">
                        {t('pages.OrderDetail.waiting_to_be_completed')}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-12433">
          {/* Customer Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12434">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12435">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12436">
                <UserIcon size={18} className="text-gray-500" data-id="element-12437" />
                {t('pages.OrderDetail.customer_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-12438">
              <div className="flex items-center gap-3 mb-2" data-id="element-12439">
                <div className="w-10 h-10 bg-[#E2231A]/10 rounded-full flex items-center justify-center text-[#9F140F] font-semibold" data-id="element-12440">
                  {order.customer.name.charAt(0).toUpperCase()}
                </div>
                <div data-id="element-12441">
                  <div className="font-medium text-gray-900" data-id="element-12442">
                    {order.customer.name}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-12443">
                    {t('pages.OrderDetail.customer')}
                    {order.id.split('-')[0]}
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-2 border-t border-gray-100" data-id="element-12444">
                <div className="flex items-start gap-3" data-id="element-12445">
                  <MailIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12446" />
                  <div data-id="element-12447">
                    <div className="text-sm text-gray-900" data-id="element-12448">
                      {order.customer.email}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12449">
                      {t('pages.OrderDetail.email')}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-12450">
                  <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12451" />
                  <div data-id="element-12452">
                    <div className="text-sm text-gray-900" data-id="element-12453">
                      {order.customer.mobileNumber}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12454">
                      {t('pages.OrderDetail.mobile')}
                    </div>
                  </div>
                </div>
                {order.customer.additionalPhone && <div className="flex items-start gap-3" data-id="element-12455">
                    <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12456" />
                    <div data-id="element-12457">
                      <div className="text-sm text-gray-900" data-id="element-12458">
                        {order.customer.additionalPhone}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-12459">
                        {t('pages.OrderDetail.additional_phone')}
                      </div>
                    </div>
                  </div>}
                <div className="flex items-start gap-3" data-id="element-12460">
                  <MapPinIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12461" />
                  <div data-id="element-12462">
                    <div className="text-sm text-gray-900" data-id="element-12463">
                      {order.customer.address}
                    </div>
                    {order.customer.address2 && <div className="text-sm text-gray-900 mt-0.5" data-id="element-12464">
                        {order.customer.address2}
                      </div>}
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-12465">
                      {t('pages.OrderDetail.shipping_address')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12466">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12467">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12468">
                <CreditCardIcon size={18} className="text-gray-500" data-id="element-12469" />
                {t('pages.OrderDetail.payment_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-12470">
              <div className="flex items-start gap-3" data-id="element-12471">
                <CreditCardIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12472" />
                <div data-id="element-12473">
                  <div className="text-sm text-gray-900" data-id="element-12474">
                    {getPaymentMethod(order.transaction)}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-12475">
                    {t('pages.OrderDetail.payment_method')}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-12476">
                <TagIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12477" />
                <div data-id="element-12478">
                  <div className="text-sm text-gray-900 capitalize" data-id="element-12479">
                    {order.paymentStatus}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-12480">
                    {t('pages.OrderDetail.payment_status')}
                  </div>
                </div>
              </div>
              {order.transaction && <div className="flex items-start gap-3" data-id="element-12481">
                  <FileTextIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12482" />
                  <div data-id="element-12483">
                    <div className="text-sm text-gray-900 break-all" data-id="element-12484">
                      {order.transaction}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12485">
                      {t('pages.OrderDetail.transaction_id')}
                    </div>
                  </div>
                </div>}
              {order.transactionStatus && <div className="flex items-start gap-3" data-id="element-12486">
                  {order.transactionStatus === 'confirmed' ? <CheckCircleIcon size={16} className="text-green-500 mt-0.5" data-id="element-12487" /> : order.transactionStatus === 'failed' ? <AlertTriangleIcon size={16} className="text-red-500 mt-0.5" data-id="element-12488" /> : <ClockIcon size={16} className="text-amber-500 mt-0.5" data-id="element-12489" />}
                  <div data-id="element-12490">
                    <div className="text-sm text-gray-900 capitalize" data-id="element-12491">
                      {order.transactionStatus}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12492">
                      {t('pages.OrderDetail.transaction_status')}
                    </div>
                  </div>
                </div>}
            </div>
          </div>

          {/* Shipping Status */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12493">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12494">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12495">
                <TruckIcon size={18} className="text-gray-500" data-id="element-12496" />
                {t('pages.OrderDetail.shipping_status')}
              </h2>
            </div>
            <div className="p-6" data-id="element-12497">
              <div className="text-sm font-medium text-gray-900 mb-4" data-id="element-12498">
                {getShippingStatus(order.status)}
              </div>
              {order.notes && <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-12499">
                  <div className="text-xs font-medium text-gray-700 mb-1" data-id="element-12500">
                    {t('pages.OrderDetail.customer_notes')}
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-12501">{order.notes}</p>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}